import { DocumentTypeEnum, DocumentType, Document, EntityType } from '@/types'
import { Box } from '@mui/material'
import { Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone, ErrorCode, DropzoneProps } from 'react-dropzone'
import { theme } from '@/styles/theme'
import { useUploadDocument } from '@/api/accounting-dealer/file'
import { queryClient } from '@/api/react-query'
import { DocumentInputContent } from './content'
import { useFormContext } from 'react-hook-form'

type FileTypes = 'pdf' | 'jpg' | 'jpeg' | 'png'

type DocumentInputProps = DropzoneProps & {
  entity: { key: string; type: EntityType }
  documents: Document[]
  documentType: DocumentType
  document_request_key?: string
  required?: boolean
  maxFiles?: number
  fieldName?: string
  label?: string
  acceptTypes?: FileTypes[]
}

const errorMessages: Record<ErrorCode, string> = {
  'file-too-large': 'Arquivo muito grande',
  'file-invalid-type': 'Tipo de arquivo inválido',
  'too-many-files': 'Máximo de arquivos atingido',
  'file-too-small': 'Arquivo muito pequeno',
}

export function DocumentInput({
  documentType,
  documents,
  entity,
  maxFiles = 5,
  required = false,
  fieldName,
  label,
  document_request_key,
  accept,
}: DocumentInputProps) {
  const [dropErrors, setDropErrors] = useState<string[] | []>([])

  const { setValue } = useFormContext()

  const acceptTypes = accept || {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
  }

  const { mutate: uploadDocument, isPending: isUploading } = useUploadDocument({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['documents', entity.key, entity.type],
      })
    },
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      const formData = new FormData()

      formData.append('file', file)

      uploadDocument({
        body: formData,
        entity_key: entity.key,
        entity_type: entity.type,
        document_type: documentType,
        document_request_key: document_request_key,
      })
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: acceptTypes,
      noDragEventsBubbling: true,
      multiple: true,
      onDropRejected: (fileRejections) => {
        const errorsMessage: string[] = []

        fileRejections.forEach((file) => {
          const errorCode = file.errors[0].code as ErrorCode
          errorsMessage.push(errorMessages[errorCode])
        })

        setDropErrors(errorsMessage)
      },
    })

  const filesFiltered =
    useMemo(
      () => documents?.filter((document) => document.type === documentType),
      [documents, documentType],
    ) || []

  useEffect(() => {
    if (!isDragReject) {
      setDropErrors([])
    }
  }, [isDragReject])

  const maxFilesReached = filesFiltered.length >= maxFiles

  useEffect(() => {
    if (!fieldName) return
    if (filesFiltered.length > 0) {
      const uploadedFileKeys = filesFiltered.map((file) => file.key)
      setValue(fieldName, uploadedFileKeys, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValue(fieldName, [], { shouldValidate: true, shouldDirty: true })
    }
  }, [filesFiltered, fieldName, setValue])

  return (
    <Box>
      <Typography mb={1}>
        {label || DocumentTypeEnum[documentType]}{' '}
        {required && (
          <Typography component="span" color="error">
            *
          </Typography>
        )}
      </Typography>
      <Box
        {...getRootProps()}
        onDragOver={(e) => {
          e.preventDefault()
        }}
        sx={{
          border: isDragActive
            ? `2px dashed ${theme.palette.primary.main}`
            : isDragReject
              ? `2px dashed ${theme.palette.error.main}`
              : '2px dashed #ccc',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: maxFilesReached ? 'default' : 'pointer',
          position: 'relative',
          transition: 'all 0.5s ease',
          '&:hover': {
            borderColor: isDragReject
              ? theme.palette.error.main
              : theme.palette.primary.main,
            backgroundColor: theme.palette.action.focus,
          },
        }}
      >
        <input {...getInputProps()} disabled={maxFilesReached} />

        <DocumentInputContent
          maxFiles={maxFiles}
          isDragActive={isDragActive}
          isUploading={isUploading}
          files={filesFiltered || []}
          entity={entity}
          acceptTypes={Object.values(acceptTypes).flat()}
        />
        <Typography color="error">{dropErrors.join(', ')}</Typography>
      </Box>
    </Box>
  )
}
